import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { SeatTagPosition } from '../../utils/audit-seat-tag-layout.util';

export interface SeatTaggedPhotoInput {
  url: string | null;
  channel: number | null;
  timestamp?: string | Date | null;
}

@Component({
  selector: 'buspro-admin-seat-tagged-photo',
  templateUrl: './seat-tagged-photo.component.html',
  styleUrls: ['./seat-tagged-photo.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SeatTaggedPhotoComponent {
  /** The photo to render (extracted JPEG from the channel's geofence clip). */
  photo = input<SeatTaggedPhotoInput | null>(null);
  /** Seat tags to overlay: just the active passenger in single mode, or every seat
   *  on this channel when the "show all tags" eye toggle is on. */
  seatTags = input<SeatTagPosition[]>([]);
  /** The current passenger's seat number — that tag is emphasized, the rest dimmed. */
  activeSeatNumber = input<string | null>(null);
  /** Direction label — drives the small banner at the top of the photo. */
  direction = input<'entry' | 'exit'>('entry');

  // Convert each tag's relative coords → CSS percentages for the overlay markers.
  // Anchor by CENTER (translate(-50%,-50%) in CSS) and size from one axis with
  // aspect-ratio:1, so each marker stays a perfect circle regardless of the photo's
  // aspect. relX/relY (the "which seat" signal) map exactly onto the rendered photo.
  renderedTags = computed(() => {
    const active = this.activeSeatNumber();
    return this.seatTags().map((tag) => ({
      seatNumber: tag.seatNumber,
      muted: !!active && tag.seatNumber !== active,
      style: {
        left: `${tag.relX * 100}%`,
        top: `${tag.relY * 100}%`,
        width: `${tag.relW * 100}%`,
        background: tag.fillColor,
      } as Record<string, string>,
    }));
  });

  banner = computed(() => (this.direction() === 'entry' ? '🛬 Entry' : '🛫 Exit'));
}
